/**
 * AuthContext
 * 
 * React Context for managing Firebase Authentication state globally.
 * Provides authentication functions and user state to all components.
 */

import { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { syncUserToLocalStorage, getAuthErrorMessage } from '../utils/auth';

const AuthContext = createContext({});

/**
 * Custom hook to access AuthContext
 * 
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider Component
 * 
 * Provides authentication state and functions to all child components.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    console.log("AUTH INIT");
  
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("AUTH STATE CHANGED:", user);
  
      setCurrentUser(user);
  
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
  
          if (userDocSnap.exists()) {
            setUserData(userDocSnap.data());
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        }
      } else {
        setUserData(null);
      }
  
      syncUserToLocalStorage(user);
      setLoading(false);
      setError(null);
    });
  
    // 🔥 KRİTİK FIX (fallback)
    const timeout = setTimeout(() => {
      console.log("AUTH TIMEOUT → forcing render");
      setLoading(false);
    }, 3000);
  
    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);
  /**
   * Sign up a new user with email and password
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} fullName - User full name
   * @param {string} country - User country (optional)
   * @returns {Promise<void>}
   */
  const signUp = async (email, password, fullName, country = '') => {
    try {
      setError(null);
      setLoading(true);

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update user profile with display name
      if (fullName && userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: fullName
        });
        // Update current user state
        setCurrentUser({ ...userCredential.user, displayName: fullName });
      }

      // Save user data to Firestore
      const userDataToSave = {
        fullName: fullName || '',
        email: email,
        country: country || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userDataToSave);
      setUserData(userDataToSave);

      // Sync to localStorage
      syncUserToLocalStorage(userCredential.user);

      setLoading(false);
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  };

  /**
   * Sign in an existing user with email and password
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<void>}
   */
  const signIn = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Sync to localStorage
      syncUserToLocalStorage(userCredential.user);

      setLoading(false);
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  };

  /**
   * Sign out the current user
   * 
   * @returns {Promise<void>}
   */
  const signOut = async () => {
    try {
      setError(null);
      setLoading(true);

      await firebaseSignOut(auth);

      // Clear localStorage auth data
      syncUserToLocalStorage(null);

      setLoading(false);
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  };

  /**
   * Send password reset email
   * 
   * @param {string} email - User email
   * @returns {Promise<void>}
   */
  const resetPassword = async (email) => {
    try {
      setError(null);
      setLoading(true);

      await sendPasswordResetEmail(auth, email);

      setLoading(false);
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  };

  const value = {
    currentUser,
    userData,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
    {loading ? <div>Loading...</div> : children}
  </AuthContext.Provider>
  );
};